// Small display helpers local to the Incentive module.
import type { IncentivePayoutType } from '../../shared/types';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// '2026-05' -> 'May 2026' ; '2026-Q2' -> 'Q2 2026'
export function formatPeriod(period: string): string {
  const q = /^(\d{4})-Q([1-4])$/.exec(period);
  if (q) return `Q${q[2]} ${q[1]}`;
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (m) {
    const idx = Number(m[2]) - 1;
    return `${MONTHS[idx] ?? m[2]} ${m[1]}`;
  }
  return period;
}

export function payoutTypeShort(t: IncentivePayoutType): string {
  return t === 'no_misses_monthly'
    ? 'Scope Performance · monthly'
    : 'Compliment · quarterly';
}

// '2026-08' -> 'August' ; '2026-Q3' -> 'Q3 2026'. Used for the live-tracker
// card headers, where Jack wants the month spelled out.
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function formatPeriodLong(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (m) return MONTHS_LONG[Number(m[2]) - 1] ?? formatPeriod(period);
  return formatPeriod(period);
}

// '2026-08-01' + '2026-09-01' -> '1–31 Aug 2026'. period_end is exclusive.
export function formatPeriodRange(start: string, endExclusive: string): string {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${endExclusive}T00:00:00`);
  e.setDate(e.getDate() - 1);
  const same = s.getMonth() === e.getMonth();
  const left = same ? `${s.getDate()}` : `${s.getDate()} ${MONTHS[s.getMonth()]}`;
  return `${left}–${e.getDate()} ${MONTHS[e.getMonth()]} ${e.getFullYear()}`;
}

// The naming convention AMs asked for: a facility is never named without the
// sub who cleans it. "Med City Dallas (MeFalty General Cleaning)".
export interface ProviderNamed {
  provider_name: string | null;
  provider_owner_name: string | null;
  worker_name: string | null;
}

export function providerLabel(r: ProviderNamed): string {
  return r.provider_name ?? r.worker_name ?? r.provider_owner_name ?? '—';
}

/** The building by its own name. The sub is shown on its own line, never in brackets
 *  after the building (Ben, 29 Oct 2026: more than one sub can work a building). */
export function facilityWithSub(
  facilityName: string | null | undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _r?: ProviderNamed,
): string {
  return facilityName ?? '—';
}
